import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { MapPin, Mail, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/anim";
import api, { formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/content";

const empty = { name: "", email: "", company: "", phone: "", message: "" };

export default function ContactFooter() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your inquiry has been sent to our team.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" data-testid="contact-section" className="bg-bg-dark text-white pt-28 md:pt-40 pb-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left: heading + address */}
          <Reveal>
            <p className="text-brand-teal text-xs font-bold uppercase tracking-[0.25em] mb-6">Get in touch</p>
            <h2 className="font-heading font-extrabold uppercase text-5xl md:text-7xl leading-[0.9] tracking-tighter">
              Let's build<br />water security.
            </h2>

            <div className="mt-14 space-y-8">
              <div className="flex gap-4">
                <MapPin className="text-brand-teal shrink-0 mt-1" size={22} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-2">Headquarters</p>
                  <p className="text-white text-lg leading-relaxed font-heading">{COMPANY.address.building}</p>
                  <p className="text-white/80">{COMPANY.address.line1}</p>
                  <p className="text-white/80">{COMPANY.address.city}</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Mail className="text-brand-teal shrink-0 mt-1" size={22} />
                <div>
                  <p className="text-white/50 text-xs uppercase tracking-[0.2em] mb-2">Inquiries</p>
                  <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-link" className="hover-underline text-white text-lg">
                    {COMPANY.email}
                  </a>
                </div>
              </div>
            </div>
          </Reveal>

          {/* Right: form */}
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name *" testid="contact-name" value={form.name} onChange={update("name")} />
                <Field label="Email *" testid="contact-email" type="email" value={form.email} onChange={update("email")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Company" testid="contact-company" value={form.company} onChange={update("company")} />
                <Field label="Phone" testid="contact-phone" value={form.phone} onChange={update("phone")} />
              </div>
              <div>
                <label className="block text-white/50 text-xs uppercase tracking-[0.2em] mb-3">Message *</label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-brand-teal focus:outline-none transition-colors resize-none"
                  placeholder="Tell us about your water needs or partnership interest..."
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="group inline-flex items-center gap-3 bg-brand-teal text-bg-dark font-semibold px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Sending..." : "Send Inquiry"}
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-24 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="US Desalination" className="h-14 w-auto brightness-0 invert" />
          </div>
          <p className="text-white/40 text-sm">© {new Date().getFullYear()} US Desalination LLC. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}

function Field({ label, testid, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="block text-white/50 text-xs uppercase tracking-[0.2em] mb-3">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border border-white/20 px-4 py-3 text-white placeholder-white/30 focus:border-brand-teal focus:outline-none transition-colors"
      />
    </div>
  );
}
